import type { Aisle } from '@shh/shared';
import { locale } from '../lib/locale';

/**
 * "Essen" (1.20): the family's own shopping list – recipes and the week plan follow. Glossary:
 * Essen = Food (area), Einkaufsliste = shopping list, Gang = aisle. German is the master.
 */
export const KITCHEN_TEXT = {
  de: {
    soon: 'Rezepte und Wochenplan kommen bald hierher.',
    addLabel: 'Was fehlt?',
    addExample: 'z. B. 2 Liter Milch',
    add: 'Auf die Liste',
    empty: 'Alles da. Was fehlt, schreibt ihr oben hinein.',
    onList: (n: number) => (n === 1 ? '1 Sache auf der Liste' : `${n} Sachen auf der Liste`),
    suggestions: 'Vorschläge',
    addThis: (item: string) => `${item} auf die Liste`,
    often: 'Oft gekauft',
    recently: 'Zuletzt gekauft',
    recentlyHint: 'Antippen, und es steht wieder auf der Liste.',
    tick: (item: string) => `${item} abhaken`,
    back: (item: string) => `${item} wieder auf die Liste`,
    holdHint: 'Gedrückt halten zum Ändern',
    edit: 'Ändern',
    editDone: 'Fertig',
    editHint: 'Tippe auf einen Eintrag, um Menge oder Gang zu ändern.',
    editItem: (item: string) => `${item} ändern`,
    name: 'Was',
    amount: 'Menge (optional)',
    amountExample: 'z. B. 2 Liter',
    aisle: 'Gang im Laden',
    save: 'Speichern',
    remove: 'Von der Liste nehmen',
    bringOnlyRemove: 'Menge und Gang ändert ihr in der Bring!-App.',
    aisles: {
      produce: 'Obst & Gemüse',
      bakery: 'Brot & Backwaren',
      dairy: 'Kühlregal',
      meat: 'Fleisch & Fisch',
      pantry: 'Vorrat',
      baking: 'Backen & Gewürze',
      frozen: 'Tiefkühl',
      snacks: 'Süßes & Snacks',
      drinks: 'Getränke',
      household: 'Drogerie & Haushalt',
      other: 'Sonstiges',
    } satisfies Record<Aisle, string>,
    shoppingList: 'Einkaufsliste',
    allThere: 'Alles da',
    fromBring: (name: string) =>
      `Liste „${name}“ aus Bring! – Änderungen in der Bring!-App erscheinen hier nach wenigen Minuten.`,
    bringAuth: 'Bring! möchte eine neue Anmeldung.',
    reconnect: 'Jetzt neu verbinden',
    bringDown: 'Bring! ist gerade nicht erreichbar – die Liste zeigt den letzten Stand.',
  },
  en: {
    soon: 'Recipes and a meal plan are coming here soon.',
    addLabel: 'What’s missing?',
    addExample: 'e.g. 2 litres of milk',
    add: 'Add to the list',
    empty: 'All there. Write what’s missing above.',
    onList: (n: number) => (n === 1 ? '1 thing on the list' : `${n} things on the list`),
    suggestions: 'Suggestions',
    addThis: (item: string) => `Add ${item} to the list`,
    often: 'Bought often',
    recently: 'Bought recently',
    recentlyHint: 'Tap it and it’s back on the list.',
    tick: (item: string) => `Tick off ${item}`,
    back: (item: string) => `Put ${item} back on the list`,
    holdHint: 'Press and hold to change',
    edit: 'Change',
    editDone: 'Done',
    editHint: 'Tap an item to change the amount or the aisle.',
    editItem: (item: string) => `Change ${item}`,
    name: 'What',
    amount: 'Amount (optional)',
    amountExample: 'e.g. 2 litres',
    aisle: 'Aisle in the shop',
    save: 'Save',
    remove: 'Take off the list',
    bringOnlyRemove: 'Change the amount and aisle in the Bring! app.',
    aisles: {
      produce: 'Fruit & veg',
      bakery: 'Bread & bakery',
      dairy: 'Chilled',
      meat: 'Meat & fish',
      pantry: 'Cupboard',
      baking: 'Baking & spices',
      frozen: 'Frozen',
      snacks: 'Sweets & snacks',
      drinks: 'Drinks',
      household: 'Toiletries & household',
      other: 'Other',
    } satisfies Record<Aisle, string>,
    shoppingList: 'Shopping list',
    allThere: 'All there',
    fromBring: (name: string) =>
      `List “${name}” from Bring! – changes in the Bring! app show up here after a few minutes.`,
    bringAuth: 'Bring! wants you to sign in again.',
    reconnect: 'Reconnect now',
    bringDown: 'Bring! can’t be reached right now – the list shows the last state.',
  },
} as const;

export const kitchenText = KITCHEN_TEXT[locale];
